function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";

// Material imports
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
// Limitless imports
import TextInput from "../text-input/TextInput";
import { withi18n } from "../../../../customer/components";
const useStyles = makeStyles(theme => ({
  containerClass: {
    display: "flex",
    flexDirection: "column"
  },
  textInput: {
    fontSize: "0.8125rem",
    height: "unset"
  },
  mandatory: {
    color: theme.palette.error.main,
    display: "inline-flex",
    position: "relative",
    bottom: 4,
    marginLeft: 3
  },
  root: {
    "& $notchedOutline": {
      border: theme.palette.border
    },
    "&:hover $notchedOutline": {
      borderColor: theme.palette.secondary.dark
    },
    "&$focused $notchedOutline": {
      borderColor: theme.palette.secondary.main,
      borderWidth: 2
    }
  },
  notchedOutline: {},
  focused: {},
  helperText: {
    minHeight: 19,
    position: "sticky"
  },
  largeHelperText: {
    minHeight: 38
  },
  solidBackground: {
    backgroundColor: theme.palette.background.paper
  },
  disabled: {
    color: theme.palette.action.disabled
  },
  dynamicBackground: {
    backgroundColor: props => props.background
  }
}));

// SSR-safe location hook
const useSafeLocation = () => {
  try {
    return useLocation();
  } catch (e) {
    return {
      pathname: '',
      search: '',
      hash: '',
      state: null,
      key: 'default'
    };
  }
};
const PortalTextInput = ({
  t,
  label,
  isMandatory,
  helperText,
  error,
  containerClass,
  color,
  largeHelperText,
  background = null,
  showHelperText,
  labelPos,
  labelEllipsis,
  errorLabel = false,
  variant = "body1",
  onChange,
  value,
  onMaliciousDetected,
  ...rest
}) => {
  const classes = useStyles({
    background
  });
  const location = useSafeLocation();
  const isOnOnboardingDetailPage = location.pathname === "/onboarding/detail";
  const [maliciousDetected, setMaliciousDetected] = useState(false);
  const handleChange = event => {
    const newValue = event.target.value;
    const xssRegex = /<script\b[^>]*>(.*?)<\/script>|javascript:|on\w+=["'].*?["']/gi;
    const hasMaliciousContent = xssRegex.test(newValue);
    setMaliciousDetected(hasMaliciousContent);
    if (typeof onMaliciousDetected === "function") {
      onMaliciousDetected(hasMaliciousContent);
    }
    if (onChange) {
      onChange(event);
    }
  };
  let portalInputClasses = rest.textInputClasses || {};
  if (color === "secondary") {
    portalInputClasses = {
      ...portalInputClasses,
      root: classes.root,
      focused: classes.focused,
      notchedOutline: classes.notchedOutline
    };
    if (background === "white") {
      portalInputClasses.root = `${classes.root} ${classes.solidBackground}`;
    } else if (background) {
      portalInputClasses.root = `${classes.root} ${classes.dynamicBackground}`;
    }
  }
  let ellipsisCSS = {};
  if (labelEllipsis) {
    ellipsisCSS = {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
  }
  return /*#__PURE__*/React.createElement("div", {
    className: containerClass || classes.containerClass
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: variant,
    color: (error || maliciousDetected) && errorLabel ? "error" : "textPrimary",
    component: "label",
    style: {
      position: labelPos,
      ...ellipsisCSS
    },
    htmlFor: rest.id,
    className: `${rest.disabled ? classes.disabled : ""}`
  }, t(label), " ", isMandatory && /*#__PURE__*/React.createElement("div", {
    className: classes.mandatory
  }, "*")), /*#__PURE__*/React.createElement(TextInput, _extends({
    variant: "outlined",
    label: "",
    inputProps: {
      className: classes.textInput
    },
    textInputClasses: portalInputClasses,
    error: (error || maliciousDetected) && errorLabel
  }, rest, {
    value: value,
    onChange: handleChange
  })), showHelperText && /*#__PURE__*/React.createElement(FormHelperText, {
    className: largeHelperText ? classes.largeHelperText : classes.helperText,
    error: error || maliciousDetected,
    style: isOnOnboardingDetailPage ? {
      position: "sticky"
    } : {}
  }, maliciousDetected ? t("Script tags or malicious content detected. Please remove to proceed here.") : t(helperText)));
};
export default withi18n(PortalTextInput);
PortalTextInput.propTypes = {
  t: PropTypes.func.isRequired,
  isMandatory: PropTypes.bool,
  label: PropTypes.string,
  variant: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  error: PropTypes.bool,
  containerClass: PropTypes.string,
  color: PropTypes.string,
  largeHelperText: PropTypes.bool,
  background: PropTypes.string,
  showHelperText: PropTypes.bool,
  labelPos: PropTypes.string,
  labelEllipsis: PropTypes.bool,
  errorLabel: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.string,
  onMaliciousDetected: PropTypes.func
};
PortalTextInput.defaultProps = {
  isMandatory: false,
  helperText: "",
  error: false,
  containerClass: "",
  color: "secondary",
  largeHelperText: false,
  background: "inherit",
  showHelperText: true,
  labelEllipsis: false,
  labelPos: "relative",
  onMaliciousDetected: false
};